import React from 'react';
import { createContext, useCallback, useContext, useEffect, useState } from 'react';
import { dataService } from '@/api';

const HealthContext = /*#__PURE__*/ createContext(null);

function mapHealthResponse(json) {
  return {
    status: json?.status ?? 'error',
    database: json?.database ?? 'unknown',
    readyState: typeof json?.readyState === 'number' ? json.readyState : null,
    version: typeof json?.version === 'string' ? json.version : null,
    service: typeof json?.service === 'string' ? json.service : null,
    dbName: typeof json?.dbName === 'string' ? json.dbName : null,
    uptimeSeconds: typeof json?.uptimeSeconds === 'number' ? json.uptimeSeconds : null,
    environment: typeof json?.environment === 'string' ? json.environment : null,
    urlSlug: typeof json?.urlSlug === 'string' ? json.urlSlug : null,
    timestamp: typeof json?.timestamp === 'string' ? json.timestamp : null,
    loading: false,
    error: null,
  };
}

export function HealthProvider({ children }) {
  const [state, setState] = useState({
    status: 'idle',
    database: 'unknown',
    readyState: null,
    version: null,
    service: null,
    dbName: null,
    uptimeSeconds: null,
    environment: null,
    urlSlug: null,
    timestamp: null,
    loading: true,
    error: null,
  });

  const refresh = useCallback(async (options = {}) => {
    setState((prev) => ({
      ...prev,
      loading: true,
      error: null,
    }));

    try {
      const json = await dataService.checkHealth(options);
      setState(mapHealthResponse(json));
    } catch (e) {
      setState({
        status: 'error',
        database: 'disconnected',
        readyState: null,
        version: null,
        service: null,
        dbName: null,
        uptimeSeconds: null,
        environment: null,
        urlSlug: null,
        timestamp: null,
        loading: false,
        error: e?.message || 'Health check failed',
      });
    }
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  return (
    <HealthContext.Provider
      value={{
        ...state,
        refresh,
      }}
    >
      {children}
    </HealthContext.Provider>
  );
}

export function useHealth() {
  const ctx = useContext(HealthContext);
  if (!ctx) {
    return {
      status: 'idle',
      database: 'unknown',
      readyState: null,
      version: null,
      service: null,
      dbName: null,
      uptimeSeconds: null,
      environment: null,
      urlSlug: null,
      timestamp: null,
      loading: false,
      error: null,
      refresh: async () => {},
    };
  }
  return ctx;
}
